import dynamic from 'next/dynamic';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { useRouter } from '@/hooks/useRouter';
import { SSR_MODE } from '@/lib/const';
import type { AppErrorEvent, WithChildren } from '@/lib/types';
import { useError } from '@/providers/ErrorProvider/ErrorProvider';

/**
 * Load the Error Page Component dynamically to reduce loading time of _app.js
 */
const ErrorPage = dynamic(() => import('@/pages/_error'), { ssr: true });

const ErrorBoundary: FC<WithChildren> = ({ children }) => {
  const { push } = useRouter();
  const [errorState, setErrorState] = useState<AppErrorEvent>({
    error: null,
    errorInfo: undefined,
  });

  useEffect(() => {
    if (!SSR_MODE) {
      const handleUnhandledError = (evt: Event) => {
        const appEvent = evt as CustomEvent<AppErrorEvent>;
        setErrorState({
          error: appEvent.detail.error,
          errorInfo: appEvent.detail.errorInfo,
        });
      };

      window.addEventListener('unhandledError', handleUnhandledError);

      return () => {
        window.removeEventListener('unhandledError', handleUnhandledError);
      };
    }
  }, []);

  useEffect(() => {
    if (errorState.error) {
      // This allows the user to go back to the previous page instead of having to reload the page
      console.error('Caught error:', errorState.error);
      console.error('Error info:', errorState.errorInfo);
      push('/500', undefined, { shallow: true });
    }
  }, [errorState]);

  return <>{children}</>;
};

const ErrorBoundaryWrapper: FC<WithChildren> = ({ children }) => {
  const { error, setError } = useError();
  const router = useRouter();

  useEffect(() => {
    const { message, source } = router.query;
    if (message && source === 'ssr') {
      setError({
        message: String(message),
        source: String(source),
      });
    }
  }, [router.query]);

  if (error && error.source === 'ssr') {
    return <ErrorPage message={error.message} />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

export default ErrorBoundaryWrapper;
